/**
 * What: Picks the sections that show a working cue while a check runs (each section that owns an edit in flight), and the phrase each shows — both chosen by the
 *   Worker, not the app. Each field/action block (and the field picker) carries its own `working` word; this looks that word
 *   up by the edit key the merchant just reported and names the section that owns the block. Every other section shows
 *   nothing new.
 * Transport / data-flow: Pure lookup over the payload. The app never composes or chooses wording: it only matches the
 *   in-flight edit key to a block the Worker already labelled. A key with no labelled block (e.g. a source refresh with no
 *   edit) lights no section.
 */
import type { BuilderBlock, BuilderEdit, ShopifyStudyBuilderEvaluationResponse } from '@testfeed/shopify-contracts'

type Section = ShopifyStudyBuilderEvaluationResponse['sections'][number]
type Entry = { phrase: string; sectionId: string }

/** The edit key the field picker reports when a concept is picked; its `working` word covers that key. */
const FIELD_PICKER_KEY = 'comparisons.add'

/** Records a block's `working` word under the edit key it reports, recursing into the containers that hold fields. */
function indexBlock(block: BuilderBlock, sectionId: string, into: Map<string, Entry[]>): void {
  if (block.kind === 'row' || block.kind === 'group') {
    for (const child of block.blocks) indexBlock(child, sectionId, into)
    return
  }
  if (block.kind === 'field-picker') {
    if (block.working)
      into.set(FIELD_PICKER_KEY, [...(into.get(FIELD_PICKER_KEY) ?? []), { phrase: block.working, sectionId }])
    return
  }
  // Retain every declared owner: product picker and variant tiles share selectedVariant. Failures belong to both;
  // the established working cue still uses the last block's phrase without guessing the edit's domain meaning.
  if ('key' in block && 'working' in block && typeof block.working === 'string')
    into.set(block.key, [...(into.get(block.key) ?? []), { phrase: block.working, sectionId }])
}

/** The key → {phrase, section} index for every block the Worker labelled with a working word. */
function buildIndex(sections: readonly Section[]): Map<string, Entry[]> {
  const index = new Map<string, Entry[]>()
  for (const section of sections) {
    if (section.header?.action) indexBlock(section.header.action, section.id, index)
    for (const block of [...section.body, ...section.status, ...section.footer]) indexBlock(block, section.id, index)
  }
  return index
}

/** Failed requests use the same Worker-labelled block ownership as pending requests, without inventing a key map. */
export function resolveEditSections(
  sections: readonly Section[],
  edits: readonly Pick<BuilderEdit, 'key'>[],
): ReadonlySet<string> {
  const index = buildIndex(sections)
  return new Set(
    edits.flatMap((edit) => {
      return (index.get(edit.key) ?? []).map((entry) => entry.sectionId)
    }),
  )
}

/**
 * Section id → the phrase it shows while `checking`. Every section that owns an edit in flight gets its own block's word
 * (an audience edit and an "Add option" press in one request light both sections); within a section the newest edit's
 * word wins. Unlabelled keys light nothing. Empty when not checking.
 */
export function resolveWorkingCues(
  sections: readonly Section[],
  pendingEdits: readonly BuilderEdit[],
  checking: boolean,
): ReadonlyMap<string, string> {
  const cues = new Map<string, string>()
  if (!checking) return cues
  const index = buildIndex(sections)
  for (const edit of pendingEdits) {
    const entry = index.get(edit.key)?.at(-1)
    if (entry) cues.set(entry.sectionId, entry.phrase)
  }
  return cues
}
